import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useMuestrasPermissions } from '../../components/muestras/LayoutMuestras';
import { getFichas, createFicha, deleteFicha, getBases, getMuestrasBase, getMarcas, getTiposProducto, getEntalles, getTelas, getHilos } from '../../services/muestrasApi';
import ExcelGrid from '../../components/muestras/ExcelGrid';
import { toast } from 'sonner';
import { FileText, Plus, Trash2, RefreshCw, X, Upload, Download, Search, AlertTriangle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MuestrasFichas = () => {
  const { user, token } = useApp();
  const { canCreate, canEdit, canDelete, isAdmin } = useMuestrasPermissions();
  
  // Permisos específicos para Fichas
  const puedeCrear = canCreate('fichas') || isAdmin;
  const puedeEliminar = canDelete('fichas') || isAdmin;
  
  const getToken = () => token;
  const [data, setData] = useState([]);
  const [bases, setBases] = useState([]);
  const [muestrasBase, setMuestrasBase] = useState([]);
  const [catalogos, setCatalogos] = useState({ marcas: [], tipos: [], entalles: [], telas: [], hilos: [] });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ id_base: '', nombre_ficha: '' });
  const [archivoFile, setArchivoFile] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fichasData, basesData, muestrasData, marcasData, tiposData, entallesData, telasData, hilosData] = await Promise.all([
        getFichas(),
        getBases(),
        getMuestrasBase(),
        getMarcas(),
        getTiposProducto(),
        getEntalles(),
        getTelas(),
        getHilos()
      ]);
      setData(fichasData || []);
      setBases(basesData || []);
      setMuestrasBase(muestrasData || []);
      setCatalogos({
        marcas: marcasData || [],
        tipos: tiposData || [],
        entalles: entallesData || [],
        telas: telasData || [],
        hilos: hilosData || []
      });
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  // Helper para obtener info de Muestra Base: id - marca - tipo - entalle - tela - hilo
  const getMuestraBaseLabel = (idBase) => {
    const base = bases.find(b => b.id_base === idBase);
    if (!base) return '-';
    
    const muestra = muestrasBase.find(m => m.id_muestra_base === base.id_muestra_base);
    if (!muestra) return `#${idBase} - Sin muestra base`;
    
    const marca = catalogos.marcas.find(x => x.id_marca === muestra.id_marca);
    const tipo = catalogos.tipos.find(x => x.id_tipo === muestra.id_tipo);
    const entalle = catalogos.entalles.find(x => x.id_entalle === muestra.id_entalle);
    const tela = catalogos.telas.find(x => x.id_tela === muestra.id_tela);
    const hilo = catalogos.hilos.find(x => x.id_hilo === muestra.id_hilo);
    
    const parts = [
      `#${idBase}`,
      marca?.nombre_marca,
      tipo?.nombre_tipo,
      entalle?.nombre,
      tela?.nombre,
      hilo?.codigo || hilo?.nombre
    ].filter(Boolean);
    
    return parts.join(' - ');
  };

  const handleCreate = async () => {
    if (!formData.id_base || !formData.nombre_ficha) { 
      toast.error('Base y nombre son requeridos'); 
      return; 
    }
    try {
      let filename = null;
      if (archivoFile) {
        const uploadData = new FormData();
        uploadData.append('file', archivoFile);
        const res = await fetch(`${API_BASE}/api/muestras/upload?folder=fichas`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: uploadData
        });
        if (res.ok) {
          const data = await res.json();
          filename = data.filename;
        }
      }
      await createFicha({ ...formData, archivo: filename });
      toast.success('Ficha creada');
      setShowModal(false);
      setFormData({ id_base: '', nombre_ficha: '' });
      setArchivoFile(null);
      loadData();
    } catch (error) { toast.error(error.message); }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await deleteFicha(deleteItem.id_ficha);
      toast.success('Ficha eliminada');
      setDeleteItem(null);
      loadData();
    } catch (error) { 
      toast.error(error.message); 
    } finally {
      setDeleting(false);
    }
  };

  const handleFileUpload = async (fichaId, file) => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await fetch(`${API_BASE}/api/muestras/fichas/${fichaId}/archivo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formDataUpload
      });
      
      if (!response.ok) throw new Error('Error subiendo archivo');
      toast.success('Archivo subido');
      loadData();
    } catch (error) { 
      toast.error(error.message);
    }
  };

  // Filtrado
  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const q = searchQuery.toLowerCase();
    return data.filter(f => {
      const label = getMuestraBaseLabel(f.id_base).toLowerCase();
      return label.includes(q) || f.nombre_ficha?.toLowerCase().includes(q);
    });
  }, [data, searchQuery, bases, muestrasBase, catalogos]);

  const columns = useMemo(() => [
    { accessorKey: 'id_ficha', header: 'ID', size: 60 },
    {
      accessorKey: 'muestra_base', header: 'MUESTRA BASE', size: 320,
      cell: ({ row }) => (
        <span className="font-medium text-gray-800 truncate block" title={getMuestraBaseLabel(row.original.id_base)}>
          {getMuestraBaseLabel(row.original.id_base)}
        </span>
      )
    },
    {
      accessorKey: 'nombre_ficha', header: 'NOMBRE FICHA', size: 200,
      cell: ({ row }) => <span className="text-gray-700">{row.original.nombre_ficha || '-'}</span>
    },
    {
      accessorKey: 'archivo', header: 'ARCHIVO', size: 150,
      cell: ({ row }) => {
        const hasFile = row.original.archivo;
        if (!hasFile) {
          return (
            <label className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg cursor-pointer text-xs font-medium transition-colors">
              <Upload className="w-3 h-3" />
              Subir
              <input
                type="file"
                accept=".pdf,.xlsx,.xls,.doc,.docx"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(row.original.id_ficha, file);
                  e.target.value = '';
                }}
              />
            </label>
          );
        }
        const ext = hasFile.split('.').pop()?.toUpperCase();
        return (
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded text-xs font-bold ${
              ext === 'PDF' ? 'bg-red-100 text-red-600' :
              ext === 'XLSX' || ext === 'XLS' ? 'bg-green-100 text-green-600' :
              'bg-blue-100 text-blue-600'
            }`}>{ext}</span>
            <a 
              href={`${API_BASE}/api/muestras/files/${hasFile}?download=true`} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="p-1 rounded bg-green-50 text-green-600 hover:bg-green-100" 
              title="Descargar"
            >
              <Download className="w-4 h-4" />
            </a>
          </div>
        );
      }
    },
    {
      id: 'acciones', header: 'ACCIONES', size: 80,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {puedeEliminar && (
            <button 
              onClick={() => setDeleteItem(row.original)} 
              className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100"
              data-testid={`delete-ficha-${row.original.id_ficha}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      )
    }
  ], [puedeEliminar, bases, muestrasBase, catalogos]);

  return (
    <div className="p-6 bg-gray-50 min-h-full" data-testid="muestras-fichas-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-orange-100 rounded-xl">
            <FileText className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Fichas Técnicas</h1>
            <p className="text-gray-400 text-sm">Gestión de fichas técnicas por base</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadData} className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">
            <RefreshCw className="w-5 h-5" />
          </button>
          {puedeCrear && (
            <button 
              onClick={() => setShowModal(true)} 
              className="px-4 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 flex items-center gap-2 font-medium"
            >
              <Plus className="w-4 h-4" />Nueva Ficha
            </button>
          )}
        </div>
      </div>

      {/* Buscador */}
      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar por muestra base o nombre..."
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 text-sm"
        />
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <ExcelGrid data={filteredData} columns={columns} loading={loading} pageSize={20} />
      </div>

      {/* Modal Crear */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Nueva Ficha Técnica</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Base *</label>
                <select 
                  value={formData.id_base} 
                  onChange={(e) => setFormData({ ...formData, id_base: e.target.value })} 
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Seleccionar Base</option>
                  {bases.map(b => (
                    <option key={b.id_base} value={b.id_base}>
                      {getMuestraBaseLabel(b.id_base)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la Ficha *</label>
                <input 
                  type="text" 
                  value={formData.nombre_ficha} 
                  onChange={(e) => setFormData({ ...formData, nombre_ficha: e.target.value })} 
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500" 
                  placeholder="Ej: Ficha de Medidas" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Archivo (opcional)</label>
                <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-orange-400 cursor-pointer">
                  <input 
                    type="file" 
                    accept=".pdf,.xlsx,.xls,.doc,.docx" 
                    onChange={(e) => setArchivoFile(e.target.files[0])} 
                    className="hidden" 
                    id="ficha-file" 
                  />
                  <label htmlFor="ficha-file" className="cursor-pointer">
                    <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500 truncate">
                      {archivoFile ? archivoFile.name : 'Clic para seleccionar archivo'}
                    </p>
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">
                Cancelar
              </button>
              <button onClick={handleCreate} className="px-4 py-2 bg-orange-600 text-white rounded-xl hover:bg-orange-700 font-medium">
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmación Eliminar */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">¿Eliminar Ficha?</h3>
              <p className="text-gray-500 mb-6">
                ¿Estás seguro de eliminar la ficha <strong>"{deleteItem.nombre_ficha}"</strong>? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-center gap-3">
                <button 
                  onClick={() => setDeleteItem(null)} 
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button 
                  onClick={confirmDelete} 
                  className="px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium disabled:opacity-50"
                  disabled={deleting}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MuestrasFichas;
